import { Modal, Stack, Group, Button, Select, Text, Center, Paper } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import MilkdownRenderer from '../shared/MilkdownRenderer';
import { JobArtifact } from '../../api/artifacts';
import { PdfStylesheet, useStylesheets } from '../../api/stylesheets';
import { useMemo } from 'react';

interface ResumePreviewModalProps {
  opened: boolean;
  onClose: () => void;
  resume: JobArtifact | null;
  allStylesheets: PdfStylesheet[];
  defaultStylesheet: PdfStylesheet | null;
}

export function ResumePreviewModal({
  opened,
  onClose,
  resume,
  allStylesheets,
  defaultStylesheet,
}: ResumePreviewModalProps) {
  // Determine which stylesheet to use
  const stylesheet = useMemo(() => {
    if (!resume) return null;

    // If resume has a stylesheet_id, find and use it
    if (resume.stylesheet_id) {
      return allStylesheets.find((s) => s.id === resume.stylesheet_id) || defaultStylesheet;
    }

    // Otherwise use the default stylesheet
    return defaultStylesheet;
  }, [resume, allStylesheets, defaultStylesheet]);

  if (!resume) return null;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Preview: ${resume.name} (v${resume.version})`}
      size="lg"
      fullScreen
    >
      <Stack gap="md" h="100%">
        {/* Stylesheet info */}
        <Group justify="space-between" align="center">
          <Text size="sm" c="dimmed">
            {stylesheet ? `Stylesheet: ${stylesheet.name}` : 'Using default styling'}
          </Text>
          <Button variant="light" size="sm" leftSection={<IconX size={16} />} onClick={onClose}>
            Close
          </Button>
        </Group>

        {/* Preview Container - styled as a document */}
        <Paper
          p="xl"
          radius="md"
          withBorder
          style={{
            flex: 1,
            overflowY: 'auto',
            backgroundColor: '#ffffff',
            color: '#000000',
            maxWidth: '900px',
            margin: '0 auto',
            width: '100%',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          }}
        >
          {/* Render markdown content with custom stylesheet */}
          <MilkdownRenderer
            content={resume.content_markdown}
            variant="resume-preview"
            customStyles={stylesheet?.css_content}
          />
        </Paper>
      </Stack>
    </Modal>
  );
}
